import { useCallback, useEffect, useRef, useState } from 'react';

const STORAGE_KEY = 'bizy-greeting-impressions';
const INTRODUCTORY_THRESHOLD = 4;

export type GreetingVariant = 'introductory' | 'familiar' | 'canceled_admin' | 'canceled_non_admin';

function getImpressionCount(): number {
	try {
		const stored = localStorage.getItem(STORAGE_KEY);
		return stored ? parseInt(stored, 10) || 0 : 0;
	} catch {
		return 0;
	}
}

function incrementImpressionCount(): number {
	const next = getImpressionCount() + 1;
	try {
		localStorage.setItem(STORAGE_KEY, String(next));
	} catch {
		// localStorage unavailable
	}
	return next;
}

export function getGreetingVariant(isCanceled: boolean, isAdmin: boolean, impressionCount: number): GreetingVariant {
	if (isCanceled) {
		return isAdmin ? 'canceled_admin' : 'canceled_non_admin';
	}
	return impressionCount < INTRODUCTORY_THRESHOLD ? 'introductory' : 'familiar';
}

export function useBizyGreeting(isVisible: boolean, isCanceled: boolean, isAdmin: boolean, options: { countsImpression?: boolean } = {}) {
	// Whether this showing advances the persisted impression counter. The idle
	// blank slate (BIZY-683) re-displays the greeting mid-conversation and must
	// NOT consume introductory impressions — only genuine empty-state openings
	// move the intro → familiar window. The displayed variant still follows the
	// current count either way.
	const { countsImpression = true } = options;
	const [impressionCount, setImpressionCount] = useState(getImpressionCount);
	const hasCountedRef = useRef(false);

	useEffect(() => {
		if (isVisible && countsImpression && !hasCountedRef.current && !isCanceled) {
			hasCountedRef.current = true;
			incrementImpressionCount();
		}
		if (!isVisible) {
			hasCountedRef.current = false;
			setImpressionCount(getImpressionCount());
		}
	}, [isVisible, isCanceled, countsImpression]);

	const variant = getGreetingVariant(isCanceled, isAdmin, impressionCount);

	return { variant, impressionCount };
}

// ─── New-segment greeter callout (BIZY-638) ──────────────────────────
//
// A distinct greeting surface from the empty-state `useBizygreeting` variant
// above: when a *new* chat segment is about to start (the server's 8h-boundary
// `isNewSegment` flag) and the member has unread People Browser moments, Bizy
// shows a friendly time-of-day callout summarizing who's worth recognizing.
// The callout is dismissed for the segment once the user sends any message.

/** Resolve the time-of-day i18n key ("Good morning/afternoon/evening"). */
export function getTimeOfDayGreetingKey(now: Date = new Date()): string {
	const hour = now.getHours();
	if (hour < 12) return 'greeter.morning';
	if (hour < 17) return 'greeter.afternoon';
	return 'greeter.evening';
}

export interface UseBizyGreeterCalloutOptions {
	/** Feature-gate (People Browser flag). When false the callout never shows. */
	enabled: boolean;
	/** Server signal: the next message would open a fresh segment, not append. */
	isNewSegment: boolean;
	/** Number of unread People Browser entries. Drives the "who to recognize" summary. */
	unreadCount: number;
	/**
	 * Total number of People Browser entries (read + unread). The connect-tools
	 * empty state only shows when this is zero — a member with moments they've
	 * already read has connected tools working, so we must not nudge them (BIZY-700).
	 */
	momentCount?: number;
	/**
	 * Count of connected connectors. When there are no moments at all we still
	 * show the callout as a low-key "connect your tools" empty state, but only
	 * for members with fewer than two connectors connected (BIZY-700). More
	 * connected connectors with no moments just means low activity, so we fall
	 * back to the standard empty state instead of a misleading connect nudge.
	 */
	connectedConnectorCount?: number;
}

export interface UseBizyGreeterCalloutResult {
	/** Whether to render the greeter callout. */
	showGreeter: boolean;
	/** Time-of-day i18n key for the greeting line. */
	timeOfDayKey: string;
	/** Dismiss the callout for the current segment (call on first message send). */
	dismiss: () => void;
}

// Below this many connected connectors, the no-moments empty state nudges the
// member to connect their tools (BIZY-700).
const CONNECT_NUDGE_MAX_CONNECTORS = 2;

export function useBizyGreeterCallout({
	enabled,
	isNewSegment,
	unreadCount,
	momentCount,
	connectedConnectorCount,
}: UseBizyGreeterCalloutOptions): UseBizyGreeterCalloutResult {
	const [dismissed, setDismissed] = useState(false);

	// Re-arm the callout whenever a fresh segment begins (false → true). Dismissal
	// is in-memory and scoped to the current segment; a later load that detects a
	// new segment boundary shows it again.
	const wasNewSegmentRef = useRef(isNewSegment);
	useEffect(() => {
		if (isNewSegment && !wasNewSegmentRef.current) setDismissed(false);
		wasNewSegmentRef.current = isNewSegment;
	}, [isNewSegment]);

	const dismiss = useCallback(() => setDismissed(true), []);

	// Show for unread moments, or — when there are no moments at all — as a
	// connect-your-tools empty state for members with few connectors connected.
	// The nudge keys off total moments (not just unread) so someone who has read
	// their moments isn't told to connect tools that are already working.
	const hasUnread = unreadCount > 0;
	const noMoments = (momentCount ?? unreadCount) === 0;
	const showConnectNudge = connectedConnectorCount != null && connectedConnectorCount < CONNECT_NUDGE_MAX_CONNECTORS && noMoments;
	const showGreeter = enabled && isNewSegment && !dismissed && (hasUnread || showConnectNudge);

	return { showGreeter, timeOfDayKey: getTimeOfDayGreetingKey(), dismiss };
}
